import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { ClubCard } from '@/components/club/ClubCard';
import { MatchCard } from '@/components/match/MatchCard';
import { EmptyState } from '@/components/ui/EmptyState';
import { Colors } from '@/constants/Colors';
import { useTheme } from '@/hooks/useTheme';
import { useFavoritesStore } from '@/stores/favoritesStore';
import { useClubStore } from '@/stores/clubStore';
import { useMatchStore, matchHasKnownKickoffTime } from '@/stores/matchStore';
import { clubConnexionCle, matchConnexionCle } from '@/utils/favoriteKeys';

const TABS = ['Mes clubs', 'Matchs suivis'] as const;

export default function FavoritesPage() {
  const navigate = useNavigate();
  const colors = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const {
    load: loadFavorites,
    clubs: favoriteClubIds,
    matches: favoriteMatchIds,
  } = useFavoritesStore();
  const {
    clubs,
    fetchClubs,
    isLoading: isLoadingClubs,
  } = useClubStore();
  const {
    matches,
    fetchMatches,
    isLoading: isLoadingMatches,
  } = useMatchStore();

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  useEffect(() => {
    if (clubs.length === 0) {
      fetchClubs();
    }
  }, [clubs.length, fetchClubs]);

  useEffect(() => {
    if (matches.length === 0) {
      fetchMatches();
    }
  }, [matches.length, fetchMatches]);

  const favoriteClubSet = useMemo(() => new Set(favoriteClubIds.map(String)), [favoriteClubIds]);
  const favoriteMatchSet = useMemo(() => new Set(favoriteMatchIds.map(String)), [favoriteMatchIds]);

  const favoriteClubs = useMemo(
    () => clubs.filter((club) => {
      const cle = clubConnexionCle(club);
      return cle ? favoriteClubSet.has(cle) : favoriteClubSet.has(String(club.idclub));
    }),
    [clubs, favoriteClubSet],
  );

  const favoriteMatches = useMemo(
    () =>
      matches.filter(
        (match) => {
          const cle = matchConnexionCle(match);
          const isFav = cle
            ? favoriteMatchSet.has(cle)
            : favoriteMatchSet.has(String(match.idmatch_football));
          return isFav && matchHasKnownKickoffTime(match);
        },
      ),
    [matches, favoriteMatchSet],
  );

  const isClubTab = activeTab === 0;
  const isLoading = isClubTab ? isLoadingClubs : isLoadingMatches;

  const handleRefresh = () => {
    if (isClubTab) fetchClubs(true);
    else fetchMatches(true);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        backgroundColor: colors.background,
      }}
    >
      <h1 style={styles.visuallyHidden}>Mes favoris</h1>
      <div
        style={{
          flexShrink: 0,
          boxSizing: 'border-box',
          width: '100%',
          padding: '0 16px',
          borderBottom: `1px solid ${Colors.primary}15`,
          backgroundColor: colors.background,
        }}
      >
        <div style={styles.tabBarRow}>
          <div style={{ display: 'flex', flex: '1 1 0%', minWidth: 0, alignItems: 'stretch' }}>
            {TABS.map((tab, i) => (
              <button
                type="button"
                key={tab}
                onClick={() => setActiveTab(i)}
                style={{
                  flex: '1 1 0%',
                  minWidth: 0,
                  paddingTop: 12,
                  paddingBottom: 12,
                  textAlign: 'center',
                  borderBottom: `2px solid ${activeTab === i ? Colors.primary : 'transparent'}`,
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderTop: 'none',
                  font: 'inherit',
                  color: 'inherit',
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 700, color: activeTab === i ? Colors.primary : colors.textSecondary }}>{tab}</span>
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, paddingRight: 0 }}>
            <button
              type="button"
              onClick={handleRefresh}
              aria-label="Actualiser"
              style={{ ...styles.iconButton, backgroundColor: colors.surface, border: 'none', cursor: 'pointer' }}
            >
              <Icon name="refresh" size={22} color={colors.text} />
            </button>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16, paddingBottom: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: Colors.primary }}>
            {isClubTab ? favoriteClubs.length : favoriteMatches.length} au total
          </span>
        </div>

        {isClubTab ? (
          <>
            {favoriteClubs.length > 0 ? (
              favoriteClubs.map((club, i) => (
                <ClubCard
                  key={club.idclub ? `${club.idclub}-${i}` : `fav-club-${i}`}
                  club={club}
                  onPress={() => navigate(`/club/${club.idclub}`)}
                />
              ))
            ) : isLoading ? (
              <div style={styles.loadingState}>
                <div className="spinner" />
              </div>
            ) : (
              <EmptyState
                icon="star-outline"
                title="Aucun club en favoris"
                subtitle="Ajoutez des clubs depuis leur fiche pour les retrouver ici."
              />
            )}
          </>
        ) : (
          <>
            {favoriteMatches.length > 0 ? (
              favoriteMatches.map((match) => (
                <MatchCard key={match.idmatch_football} match={match} />
              ))
            ) : isLoading ? (
              <div style={styles.loadingState}>
                <div className="spinner" />
              </div>
            ) : (
              <EmptyState
                icon="calendar-outline"
                title="Aucun match suivi"
                subtitle="Ajoutez des matchs en favoris depuis leur fiche pour les retrouver ici."
              />
            )}
          </>
        )}
      </div>

    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  visuallyHidden: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0,0,0,0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  tabBarRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    width: '100%',
    minWidth: 0,
    gap: 8,
  },
  iconButton: { width: 40, height: 40, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  loadingState: {
    minHeight: 180,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};
